import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useClientDataCounts,
  useResetClientData,
  type ClientDataCounts,
  type ResetScope,
} from './useClientReset';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  clientId: string;
  clientName: string;
  scope: ResetScope;
}

const LABELS_FINANCIAL: { key: keyof ClientDataCounts; label: string }[] = [
  { key: 'transactions', label: 'Transações' },
  { key: 'documents', label: 'Documentos / anexos' },
  { key: 'agreement_payments', label: 'Pagamentos de convênios' },
  { key: 'billing_invoices', label: 'Faturas de cobrança' },
  { key: 'card_splits', label: 'Splits de cartão' },
];

const LABELS_COMPLETE_EXTRA: { key: keyof ClientDataCounts; label: string }[] = [
  { key: 'counterparties', label: 'Fornecedores/Clientes' },
  { key: 'categories', label: 'Categorias' },
  { key: 'cost_centers', label: 'Centros de custo' },
  { key: 'accounts', label: 'Contas bancárias' },
  { key: 'agreements', label: 'Convênios' },
  { key: 'treatment_packages', label: 'Pacotes de tratamento' },
  { key: 'patients', label: 'Pacientes' },
  { key: 'card_machines', label: 'Maquininhas' },
  { key: 'client_events', label: 'Eventos / timeline' },
];

export function ClientResetConfirmDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  scope,
}: Props) {
  const { data: counts, isLoading: loadingCounts } = useClientDataCounts(open ? clientId : null);
  const reset = useResetClientData();
  const [confirmName, setConfirmName] = useState('');

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- limpa o campo de confirmação ao reabrir o diálogo
    if (open) setConfirmName('');
  }, [open]);

  const items =
    scope === 'financial' ? LABELS_FINANCIAL : [...LABELS_FINANCIAL, ...LABELS_COMPLETE_EXTRA];
  const totalToDelete = counts ? items.reduce((s, i) => s + (counts[i.key] ?? 0), 0) : 0;

  const nameMatches = confirmName.trim() === clientName.trim();
  const canConfirm = nameMatches && !reset.isPending && !loadingCounts;

  const handleConfirm = async () => {
    try {
      await reset.mutateAsync({ clientId, scope });
      onOpenChange(false);
    } catch {
      // toast já mostrado pelo hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-brand text-expense">
            <AlertTriangle size={20} />
            {scope === 'financial' ? 'Resetar financeiro' : 'Resetar TUDO do cliente'}
          </DialogTitle>
          <DialogDescription>
            {scope === 'financial'
              ? 'Apaga as movimentações do cliente. Cadastros (contas, categorias, centros de custo, contrapartes) ficam intactos.'
              : 'Apaga TUDO: movimentações + todos os cadastros. Só o cliente em si é mantido.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg border border-expense/30 bg-expense/5 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-expense">
              Será apagado de <span className="font-bold">{clientName}</span>:
            </p>
            {loadingCounts ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 size={14} className="animate-spin" />
                Carregando contagens...
              </div>
            ) : counts ? (
              <ul className="space-y-0.5 text-sm">
                {items.map((i) => (
                  <li key={i.key} className="flex justify-between text-foreground">
                    <span>{i.label}</span>
                    <span className="font-medium tabular-nums">{counts[i.key] ?? 0}</span>
                  </li>
                ))}
                <li className="mt-2 flex justify-between border-t border-expense/20 pt-2 font-semibold">
                  <span>Total</span>
                  <span className="tabular-nums text-expense">{totalToDelete}</span>
                </li>
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Não foi possível carregar contagens.</p>
            )}
          </div>

          <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
            <strong>Importante:</strong> os saldos das contas continuarão mostrando o histórico
            antigo até novos lançamentos serem feitos. É soft-delete: dados ficam invisíveis para o
            app, mas o admin pode recuperar via SQL se necessário.
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reset-confirm">
              Para confirmar, digite o nome do cliente:{' '}
              <span className="font-semibold">{clientName}</span>
            </Label>
            <Input
              id="reset-confirm"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={clientName}
              autoComplete="off"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={reset.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="bg-destructive font-semibold text-destructive-foreground hover:bg-destructive/90"
          >
            {reset.isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Apagando...
              </>
            ) : (
              `Apagar ${totalToDelete > 0 ? totalToDelete : ''} ${totalToDelete === 1 ? 'item' : 'itens'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
